import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Type, effect, input, signal, untracked, ViewEncapsulation } from '@angular/core';

/**
 * Synchronous host for a lazily-loaded relation-board component. The RelationBoardMount contract
 * types `component` as a plain `Type` (mounted synchronously by the engine's tab host), but the
 * concrete board components are code-split behind dynamic import()s. This host bridges the two: it
 * is the synchronous Type the mount exposes, takes the lazy `loader` + reactive `inputs`, resolves
 * the real component once, and renders it through NgComponentOutlet. Inputs stay reactive so the
 * mounted board tracks record changes without a remount.
 *
 * Layout: `display: contents` only — no absolute/size chrome. entity-dashboard already uses
 * `display: contents` + absolute w-page against the nearest positioned ancestor (page-body).
 * An intermediate sized box (or absolute host) collapses the grid / clips the board.
 */
@Component({
  selector: 'lazy-relation-board-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgComponentOutlet],
  styles: [`
    lazy-relation-board-host {
      display: contents;
    }
  `],
  template: `@if ($resolved(); as component) {
    <ng-container *ngComponentOutlet="component; inputs: $inputs()"></ng-container>
  }`,
})
export class LazyRelationBoardHostComponent {

  public readonly $loader = input.required<() => Promise<Type<unknown>>>({ alias: 'loader' });
  public readonly $inputs = input<Readonly<Record<string, unknown>>>({}, { alias: 'inputs' });

  protected readonly $resolved = signal<Type<unknown> | null>(null);

  public constructor() {

    // Resolve the lazy component whenever the loader identity changes (a swapped board reloads once).
    effect(() => {
      const loader = this.$loader();
      untracked(() => {
        this.$resolved.set(null);
        loader().then(type => this.$resolved.set(type));
      });
    });
  }
}
